import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Hearticle, PubMedArticle, InteractionDTO, InteractionType, SharePlatform, HearticleTranslation } from '../types';
import axios from '../axios';
import logo from '../assets/images/logo_with_text.png';
import atom from '../assets/images/atom.png';
import header from '../assets/videos/header.mp4';

import RelatedStudies from './RelatedStudies';
import ShareButtons from './ShareButtons';

import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { Button } from '../components/Button';
import DemocratisingScience from './DemocratisingScience';
import { useTranslation } from 'react-i18next';
import PlayButton from './PlayButton';
import DeleteHearticleButton from './DeleteHearticleButton';


const FullHearticlePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const currentLanguage = useSelector((state: RootState) => state.language.currentLanguage);
  const [hearticle, setHearticle] = useState<Hearticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [readStartTime, setReadStartTime] = useState<number>(Date.now());
  const headlineRef = useRef<HTMLHeadingElement>(null);

  const hasReviewPermission = useSelector((state: RootState) => 
    state.auth.user?.roles?.includes('PERMISSION_REVIEW_HEARTICLES') ?? false
  );
  
  const getCurrentTranslation = useMemo((): HearticleTranslation | null => {
    if (!hearticle) return null;
    const translation = hearticle.translations.find(t => t.languageCode === currentLanguage);
    return translation || hearticle.translations[0];
  }, [hearticle, currentLanguage]);

  useEffect(() => {
    const fetchHearticle = async () => {
      try {
        const response = await axios.get(`/api/public/hearticle/${id}`);
        console.log("hearticle: ", response.data)
        setHearticle(response.data);
        setLoading(false);
        
        // Scroll to headline after hearticle is loaded
        setTimeout(() => {
          if (headlineRef.current) {
            const yOffset = -100; // Pixels above the headline
            const element = headlineRef.current;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        }, 100);
      } catch (err) {
        setError('Failed to fetch hearticle');
        setLoading(false);
      }
    };

    fetchHearticle();
  }, [id]);

  const recordReadInteraction = async () => {
    try {
      const interaction: InteractionDTO = {
        hearticleId: Number(id),
        interactionType: InteractionType.READ,
      };
      await axios.post('/api/interactions', interaction);
    } catch (error) {
      console.error('Failed to record read interaction:', error);
    }
  };

  useEffect(() => {
    if (id) {
      recordReadInteraction();
      setReadStartTime(Date.now());
    }
  
    return () => {
      if (id) {
        const timeSpent = Math.floor((Date.now() - readStartTime) / 1000);
        
        const interaction: InteractionDTO = {
          hearticleId: Number(id),
          interactionType: InteractionType.READ,
          timeSpentSeconds: timeSpent
        };
  
        axios.post('/api/interactions', interaction).catch(error => {
          console.error('Failed to record time spent:', error);
        });
      }
    };
  }, [id]);


  const handleRelatedArticleClick = async (pmid: string) => {
    if (id) {
      try {
        const interaction: InteractionDTO = {
          hearticleId: Number(id),
          interactionType: InteractionType.VIEW_RELATED
        };
        await axios.post('/api/interactions', interaction);
      } catch (error) {
        console.error('Failed to record related article interaction:', error);
      }
    }
  };

  const EnhancedShareButtons: React.FC<{ hearticleId: string }> = ({ hearticleId }) => {
    const handleShare = async (platform: string) => {
      // Record share interaction if user is authenticated
      try {
        const interaction: InteractionDTO = {
          hearticleId: Number(hearticleId),
          interactionType: InteractionType.SHARE,
          sharePlatform: platform as SharePlatform
        };
        await axios.post('/api/interactions', interaction);
      } catch (error) {
        console.error('Failed to record share interaction:', error);
      }

      // Original share functionality
      let url = '';
      switch (platform) {
        case 'instagram':
          navigator.clipboard.writeText(`${window.location.origin}/hearticle/${hearticleId}`);
          alert('Link copied to clipboard. You can now share it on Instagram.');
          return;
        case 'linkedin':
          url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
            `${window.location.origin}/hearticle/${hearticleId}`
          )}`;
          break;
        case 'twitter':
          url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
            `${window.location.origin}/hearticle/${hearticleId}`
          )}&text=${encodeURIComponent('Check out this interesting Hearticle!')}`;
          break;
        case 'email':
          url = `mailto:?subject=${encodeURIComponent(
            'Check out this interesting Hearticle!'
          )}&body=${encodeURIComponent(`${window.location.origin}/hearticle/${hearticleId}`)}`;
          break;
        case 'sms':
          url = `sms:?body=${encodeURIComponent(
            `Check out this interesting Hearticle! ${window.location.origin}/hearticle/${hearticleId}`
          )}`;
          break;
      }
      if (url) window.open(url, '_blank');
    };

    return <ShareButtons hearticleId={hearticleId} onShare={handleShare} />;
  };

  const refMap = useMemo(() => {
    if (!hearticle || !getCurrentTranslation) return {};
    let refCounter = 1;
    const map: { [key: string]: number } = {};
    const translation = getCurrentTranslation;
    const allText = translation.mainTextBody + ' ' + 
                   translation.mainActions.join(' ') + ' ' + 
                   translation.harmfulActions.join(' ');
    const refRegex = /\[Ref(\d+)(?:,\s*Ref(\d+))*\]/g;
    let match;
    while ((match = refRegex.exec(allText)) !== null) {
      match.slice(1).filter(Boolean).forEach(pmid => {
        if (!map[pmid]) {
          map[pmid] = refCounter++;
        }
      });
    }
    return map;
  }, [hearticle, getCurrentTranslation]);


  if (loading) {
    return <div>Loading...</div>;
  }

  // Early return with error state
  if (error || !hearticle || !getCurrentTranslation) {
    return <div>Error: {error || t('hearticle.notFound')}</div>;
  }

  const getImageUrl = (name: string | undefined, size: 'big' | 'small' = 'big') => {
    if (!name) return '';
    let imageName = name.toLowerCase()
        .replace(/ /g, '_')
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        + '.png';
    imageName = size === 'big' ? 'big/' + imageName : imageName;
    return `${process.env.REACT_APP_API_URL}/uploads/topic_images/${imageName}`;
  };
  
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    target.src = `${process.env.REACT_APP_API_URL}/uploads/topic_images/nutrition_medicine.png`;
    target.onerror = null; // Prevent infinite loop if default image also fails
  };

  
  const renderFormattedText = (text: string) => {
    const lines = text.split('\n');
    return lines.map((line, lineIndex) => {
      const elements: React.ReactNode[] = [];
      let lastIndex = 0;
      const pattern = /(\*\*(.*?)\*\*)|(\*(.*?)\*)|(__(.*?)__)|(_(.*?)_)|(\[Ref\d+(?:,\s*Ref\d+)*\])|(\{(.*?)\}\s*)\[EXP:(.*?)\]/g;
      let match;
  
      while ((match = pattern.exec(line)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
          elements.push(line.substring(lastIndex, match.index));
        }
  
        // Process the match
        if (match[1]) {
          // **bold**
          elements.push(
            <strong key={`${lineIndex}-${match.index}-bold`}>
              {match[2]}
            </strong>
          );
        } else if (match[3]) {
          // *italic*
          elements.push(
            <em key={`${lineIndex}-${match.index}-italic`}>
              {match[4]}
            </em>
          );
        } else if (match[5]) {
          // __underline__
          elements.push(
            <u key={`${lineIndex}-${match.index}-underline`}>
              {match[6]}
            </u>
          );
        } else if (match[7]) {
          // _italic_
          elements.push(
            <em key={`${lineIndex}-${match.index}-italic-underscore`}>
              {match[8]}
            </em>
          );
        } else if (match[9]) {
          // [Ref...]
          const refPart = match[9];
          const refs = refPart
            .slice(1, -1)
            .split(',')
            .map((ref) => ref.trim().slice(3));
          elements.push(
            <sup key={`${lineIndex}-${match.index}-ref`}>
              {refs.map((pmid, index) => {
                const article = hearticle?.relatedPubMedArticles.find(
                  (a) => a.pmid === pmid
                );
                return (
                  <React.Fragment key={index}>
                    {index > 0 && ','}
                    <span
                      className="text-blue-500 cursor-pointer text-xs align-super ml-0.5"
                      title={article ? article.title : 'Article not found'}
                      onClick={() => {
                        if (article && article.url) {
                          window.open(article.url, '_blank');
                        }
                      }}
                    >
                      [{refMap[pmid]}]
                    </span>
                  </React.Fragment>
                );
              })}
            </sup>
          );
        } else if (match[10]) {
          // {words}[EXP:...]
          const words = match[11];
          const explanation = match[12];
          elements.push(
            <span
              key={`${lineIndex}-${match.index}-exp`}
              className="text-blue-500 cursor-pointer relative group"
              onClick={(e) => {
                const tooltip = e.currentTarget.querySelector('.tooltip');
                if (tooltip) {
                  tooltip.classList.toggle('visible');
                }
              }}
            >
              {words}
              <span className="tooltip invisible group-hover:visible absolute bottom-full left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-black text-white text-sm mb-2 z-50 max-w-[280px] whitespace-normal break-words md:max-w-[24rem] lg:break-normal lg:w-max">
                {explanation.trim()}
                <span className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-black rotate-45"></span>
              </span>
            </span>
          );
        
        }
  
        lastIndex = pattern.lastIndex;
      }
  
      // Add any remaining text after the last match
      if (lastIndex < line.length) {
        elements.push(line.substring(lastIndex));
      }
  
      return (
        <React.Fragment key={lineIndex}>
          {elements}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };
  
  const translation = getCurrentTranslation as HearticleTranslation;


  return (
    <div>
      <div className="mx-auto md:p-4 mx-0 lg:mx-4 font-sans bg-white">
        {/* Header */}
        <header className="flex justify-between p-4 lg:p-0 items-center lg:mb-4">
          <div className="flex items-center">
            <img src={logo || ''} alt="HeartPort logo" className="mr-2 w-auto h-14 md:w-auto md:h-auto" />
          </div>
        </header>

        {/* Main Title and Subtext */}
        <h1 className="text-3xl lg:text-8xl font-bold mx-4 mb-4 lg:mt-10 lg:mx-0 pt-4 text-[#49ABDB]">
          DEMOCRATIZING SCIENCE
        </h1>
        <div className="absolute right-0 md:right-10 md:top-72 transform lg:w-[300px] lg:h-[300px] w-[200px] h-[200px] -translate-y-[50%] hidden md:block">
      <video 
        src={header} 
        autoPlay 
        loop 
        muted 
        playsInline 
        className="w-full h-full rounded-full"
        style={{
          objectFit: 'cover', // Ensures the content fits inside the circular frame
        }}
      />
    </div>
        <p className="pb-6 lg:pb-12 text-sm lg:text-xl m-4 lg:m-0 text-[#5B5B5B] font-bold italic">
          {t('heartport.subtitle')}
        </p>

        {/* Image */}
        <div className="w-full flex justify-center relative z-10">
          <img
            src={getImageUrl(hearticle.topic.name)}
            onError={handleImageError}
            alt="Topic Image"
            className="w-72 h-72 xl:w-[36rem] xl:h-[36rem]"
          />
        </div>

        {/* div overlapping image */}
        <div className="bg-[#d0e6ef] p-4 relative mt-[-9rem] md:mt-[-18rem] z-0 sm:mt-4">
          <div className="absolute top-4 left-4 md:mx-6 md:pt-0 pt-36">
            <DemocratisingScience />
          </div>
          {/* Spacer to adjust for the image */}
          <div className="h-72 md:h-64"></div>

          {/* Three columns */}
          <div className="flex flex-col md:flex-row md:mx-6">
            {/* Left third */}
            <div className="w-full md:w-1/3 mb-4 md:mb-0 mt-4 sm:mt-6"> {/* Add top margin for mobile */}
              <h4 className="text-xl md:text-3xl font-bold text-black mb-2 text-[#111D4E]">YOUR BREAKING HEARTICLE:</h4>
              <div
                className={`px-2 py-1 rounded inline-block text-black mb-2`}
                style={{ backgroundColor: hearticle.topic.color}}
              >
                {hearticle.topic.name}
              </div>
              <h5 ref={headlineRef} className="text-4xl md:text-4xl font-bold text-[#49ABDB] mb-2">{translation?.headline}</h5>
              
              <p className="mb-2 text-2xl font-semibold text-black italic">
                {translation?.keywords?.join(' - ') || t('noKeywordsAvailable')}
              </p>
              
              {/* Now we use the pre-computed permission */}
              {hasReviewPermission && (
                <div className="flex items-center mt-4">
                  <Button
                    onClick={() => navigate(`/review/translation/${hearticle.id}`)}
                    variant="primary"
                  >
                    {t('review.reviewTranslation')}
                  </Button>
                  <DeleteHearticleButton 
                    hearticleId={hearticle.id} 
                    headline={translation.headline} 
                  />
                </div>
              )}
            </div>

            {/* Middle third */}
            <div className="w-full md:w-1/3 flex justify-center mb-4 md:mb-0 md:mt-16">
            <PlayButton 
              hearticleId={hearticle.id} 
              translationId={translation.id}
            />
            </div>

            {/* Right third */}
            <div className="w-full md:w-1/3 flex flex-col items-center">
              <p className="mb-2 text-2xl md:text-3xl text-white font-bold">SHARE HEARTICLE</p>
              <EnhancedShareButtons hearticleId={id || ''} />
            </div>
          </div>

          {/* BUSINESS ESSENTIALS heading */}
          <h3 className="text-2xl font-bold text-[#49ABDB] mb-2 mt-8 md:mx-6">BUSINESS ESSENTIALS</h3>

          {/* Main body and action feed */}
          <div className="flex flex-col md:flex-row">
            {/* Main body */}
            <div className="w-full md:w-2/3 pr-0 md:pr-4 mb-4 md:mb-0 md:mx-6">
              <p className="text-lg md:text-base text-left text-black mb-4 whitespace-pre-wrap">
                {renderFormattedText(translation.mainTextBody)}
              </p>
            </div>

            {/* Action feed */}
            <div className="w-full md:w-1/3 md:mx-4">
              <div className="bg-gradient-to-r from-[#49ABDB] to-[#A0C9DA] p-4">
                <div className="mb-4">
                  <h3 className="text-4xl font-bold mb-6 mt-4 text-white">{t('heartport.actionFeed')}</h3>
                  <br />
                  
                  {/* Main Actions - only show if there are items */}
                  {translation.mainActions.length > 0 && (
                    <>
                      <h4 className="text-2xl font-bold mb-6 text-white italic">{t('heartport.helpful')}</h4>
                      <ul className="list-disc list-inside text-white text-xl">
                        {translation.mainActions.map((action, index) => (
                          <li key={index} className="whitespace-pre-wrap mb-4 font-semibold">
                            {renderFormattedText(action)}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {/* Centered Atom Image - only show if either list has items */}
                  {(translation.mainActions.length > 0 || translation.harmfulActions.length > 0) && (
                    <div className="flex justify-center my-8">
                      <img src={atom} alt="Atom" className="w-16 h-16" />
                    </div>
                  )}

                  {/* Harmful Actions - only show if there are items */}
                  {translation.harmfulActions.length > 0 && (
                    <>
                      <h4 className="text-2xl font-bold mb-6 text-white italic">{t('heartport.harmful')}</h4>
                      <ul className="list-disc list-inside text-white text-xl">
                        {translation.harmfulActions.map((action, index) => (
                          <li key={index} className="whitespace-pre-wrap mb-4 font-semibold">
                            {renderFormattedText(action)}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Related Studies at the end */}
          <RelatedStudies studies={hearticle.relatedPubMedArticles} />

          
        </div>
      </div>
      <div className="w-full">
            {/* Mission Statement Section */}
            <div className="bg-[#90C4D9] py-16 px-4 mt-12">
              <div className="container mx-auto max-w-4xl text-center">
                <h2 className="text-3xl md:text-4xl font-bold text-white">
                  {t('hearticle.missionStatement', 'We fight disease with the power of scientifically reviewed health essentials')}
                </h2>
              </div>
            </div>

            {/* Additional Share Buttons Section */}
            <div className="py-12">
              <div className="container mx-auto px-4">
                <div className="flex flex-col items-center">
                <div className="w-full md:w-1/3 flex flex-col items-center">
                  <p className="mb-2 text-2xl md:text-3xl text-white font-bold">SHARE HEARTICLE</p>
                  <EnhancedShareButtons hearticleId={id || ''} />
                </div>
                </div>
              </div>
            </div>
          </div>
    </div>
  );
};

export default FullHearticlePage;